import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "ttfm_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (err) {
      console.warn("[cart] failed to read persisted cart:", err);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("[cart] failed to persist cart:", err);
    }
  }, [items]);

  const add = useCallback((product, variant) => {
    setItems((prev) => {
      const cartKey = `${product.id}::${variant?.id || "default"}`;
      if (prev.find((p) => p.cartKey === cartKey)) return prev;
      return [
        ...prev,
        {
          cartKey,
          id: product.id,
          title: product.title,
          price: variant?.price ?? product.price,
          image_url: variant?.image_url || product.image_url,
          category: product.category,
          affiliate_url: product.affiliate_url,
          variant_id: variant?.id || null,
          variant_name: variant?.name || null,
        },
      ];
    });
    setOpen(true);
  }, []);

  const remove = useCallback((cartKey) => {
    setItems((prev) => prev.filter((p) => p.cartKey !== cartKey));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.length;
    const total = items.reduce((s, p) => s + (p.price || 0), 0);
    return { items, count, total, add, remove, clear, open, setOpen };
  }, [items, add, remove, clear, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
